"use client";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogTitle, DialogContent, IconButton, Typography, TextField, Alert, CircularProgress, Box } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import providers from "../../../index.js";
import { useWebAppContext } from "../../../providers/WebAppProvider.js";
import dayjs from "@metrojs/dayjs";
import logjs from "@metrojs/logjs";

import { width } from "@mui/system";

const log = new logjs("QaabDialog");

import { useQaabDialog } from "./useQaabDialog.js";
import s from "./styles.js";

function QaabDialog({ open, onClose, params = {} }) {
  log.debug("Start QaabDialog params", params);
  const { mitsumoriNo, raid = null, paid = null, isQuestion = true, requestLimit: requestLimitParam = null } = params;
  const { state, actions, params: contextParams, searchParams: contextSearchParams } = useWebAppContext();

  const initState = {
    mitsumoriNo: mitsumoriNo,
    raid: raid,
    paid: paid,
  };

  const [formData, formProps] = useQaabDialog(initState, state, actions);
  const { form, requestData, qaList, qaCnt, loading, isShowInit, isAnswerOpen, isShowConfirm, isShowFinish, mikaitoList, isShowMikaitoList, selectedQa } = formData;

  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [questionText, setQuestionText] = useState("");
  const doubleClickRef = useRef(false);

  useEffect(() => {
    if (open && mitsumoriNo && raid && paid) {
      log.debug(`Start QaDialog useEffect open mitsumoriNo: ${mitsumoriNo} raid: ${raid} paid: ${paid}`);
      formProps.init(mitsumoriNo, raid, paid);
    }
  }, [open, mitsumoriNo, raid, paid]);

  // 質問可能期間（qa_btn1 表示条件と同様: 見積期限+1ヶ月以内）
  const canAskQuestion = useMemo(() => {
    const limit = requestLimitParam || requestData?.Request_Limit;
    if (!limit) return false;
    return dayjs(limit).add(1, "month").isAfter(dayjs());
  }, [requestLimitParam, requestData?.Request_Limit]);

  const handleSend = async () => {
    if (doubleClickRef.current || sending) return;
    doubleClickRef.current = true;
    setSending(true);

    try {
      const payload = {
        mode: "finish",
        MitsumoriNo: qa?.MitsumoriNo || mitsumoriNo,
        CorpID: qa?.CorpID || corpId,
        QAnaiyo: qa?.QAnaiyo || "",
        RAID: qa?.RAID || "",
        PAID: qa?.PAID || "",
      };
      if (onConfirm) await onConfirm(payload);
    } finally {
      setSending(false);
      doubleClickRef.current = false;
    }
  };

  const handleEdit = async () => {
    if (onEdit) {
      onEdit({ mode: "edit" });
    }
  };

  const handleQuestionSubmit = async () => {
    if (!questionText.trim()) {
      return;
    }
    if (doubleClickRef.current || sending) return;
    doubleClickRef.current = true;
    setSending(true);

    try {
      const payload = {
        mode: "confirm",
        MitsumoriNo: mitsumoriNo,
        CorpID: corpId,
        QAnaiyo: questionText,
        RAID: "",
        PAID: "",
      };
      if (onConfirm) await onConfirm(payload);
    } finally {
      setSending(false);
      doubleClickRef.current = false;
    }
    try {
      const info = {
        to: "direct_order@kronometro.co.jp",
        // to: "tdaigo56@gmail.com",
        subject: "DirectOrder Test Mail",
        text: questionText,
      };
      formProps.sendQaMail(info);
    } catch (e) {
      log.error(e);
      throw e;
    }
  };

  const handleSendQaMail = async () => {
    try {
      const info = {
        to: "direct_order@kronometro.co.jp",
        // to: "tdaigo56@gmail.com",
        subject: "DirectOrder Test Mail",
        text: "test message",
      };
      formProps.sendQaMail(info);
    } catch (e) {
      log.error(e);
      throw e;
    }
  };

  const close = () => {
    setError("");
    onClose && onClose();
  };

  // 質問選択時の処理
  const handleSelectQa = (qa) => {
    log.debug(`handleSelectQa isAnswerOpen: ${isAnswerOpen} qa:`, qa);
    formProps.selectQa(qa);
    formProps.setForm({...form, QAnaiyo: ""});
    setError("");
  };

  const scrollToQuestionForm = () => {
    document.getElementById("qaab")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  log.debug(`============================ formData:`, formData);
  log.debug(`============================ requestData:`, requestData);

  // QA一覧 
  const renderFAQSection = () => {
    return (
      <s.Faq>
        <h3> 質問合計 {qaCnt}件
          {isQuestion && canAskQuestion && (
            <span style={{ float: "right" }}>
              <s.BtnGray onClick={scrollToQuestionForm}>新しく質問をする</s.BtnGray>
            </span>
          )}
        </h3>

        {qaCnt === 0 ? (
          <p>{qaCnt ? "回答済みの" : ""}質問はまだありません。</p>
        ) : (
          <dl>
            {qaList.map((Q, idx) => {
              log.debug(`Q:${Q} idx:${idx}`, Q);
              return (
                <div key={idx}>
                  <dt>
                    <span className="que">Q</span>
                    質問{Q.Q_no}　{dayjs(Q.created).format("M月D日 H時mm分")}
                    {Q.Todofuken && ` (${Q.Todofuken})`}
                  </dt>
                <dd>
                  <p>
                    {(Q.QAnaiyo || "").split("\n").map((line, i) => (
                      <span key={i}>
                        {line}
                        <br />
                      </span>
                    ))}
                  </p>
                  {Q.A?.length > 0 ? (
                    <dl>
                      {Q.A.map((A, j) => (
                        <div key={j}>
                          <dt>
                            <span className="ans">A</span>
                            回答{A.Q_no}　{dayjs(A.created).format("M月D日 HH時mm分")}
                            {A.Todofuken && ` (${A.Todofuken})`}
                          </dt>
                          <dd>
                            {A.QAnaiyo}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  ) : (
                    <p>回答はまだありません。</p>
                  )}
                </dd>
                </div>
              );
            })}
          </dl>
        )}
      </s.Faq>
    );
  };

  const isModal = false;
  return (
    <s.Dialog open={open} onClose={close} maxWidth="md" fullWidth
      disableEnforceFocus={!isModal}
      hideBackdrop={isModal}
      style={!isModal ? { pointerEvents: "none", zIndex: 1400 } : { pointerEvents: "auto" }}
      BackdropProps={
        !isModal
          ? {
              style: { pointerEvents: "none" },
            }
          : {}
      }
      slotProps={
        !isModal
          ? {
              container: { style: { pointerEvents: "none" } },
            }
          : undefined
      }
      PaperProps={
        !isModal
          ? {
              style: { pointerEvents: "auto" },
            }
          : {}
      }>
      {isQuestion ? (
        <>
          {/* 質問用 */}
          <s.DialogTitle>
            <s.FlexBetween>
              <s.Title>販売先({requestData?.Todofuken || ""})へ質問をする</s.Title>
              <IconButton onClick={close} size="small">
                <CloseIcon />
              </IconButton>
            </s.FlexBetween>
          </s.DialogTitle>

          <DialogContent sx={{ p: 3, background: "url(/images/bg2.png) 0 0" }}>
            <s.Container>
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
              {/* 注文詳細情報 */}
              <s.OrderDetail>
                <div className="wrap">
                  <dl>
                    <dt>見積番号</dt>
                    <dd>{mitsumoriNo}</dd>
                    <dt>車名</dt>
                    <dd>{requestData?.CarName}</dd>
                    <dt>車輌型式</dt>
                    <dd>{requestData?.Sharyo_Katashiki}</dd>
                    <dt>年式</dt>
                    <dd>{requestData?.Sharyo_Nenshiki}</dd>
                    <dt className="order">見積回答部品</dt>
                    <dd>{requestData?.PartsName}</dd>
                  </dl>
                </div>
              </s.OrderDetail>

              {/* QA入力 */}
              {isShowInit && (
                <>
                  {renderFAQSection()}
                  <s.Faq>
                    {/* 質問入力エリア */}
                    {canAskQuestion && (
                      <div>
                        <h3 id="qaab">質問をする</h3>
                        <p>
                          販売先({requestData?.Todofuken || ""})への質問を入力し「確認する」ボタンを押して下さい。
                          <br />
                          <span style={{ color: "#666", fontSize: "14px" }}>例)「再塗装は必要でしょうか」「検査記録簿はありますか」</span>
                        </p>

                        {requestData?.error && (
                          <div className="error">
                            <p className="error">{requestData?.error}</p>
                          </div>
                        )}

                        <div style={{ color: "red", fontWeight: "bold", margin: "10px 0" }}>値引き交渉は厳禁です。削除させて頂く場合がございます。</div>
                        {formProps.input_QAnaiyo(
                          {
                            label: "質問内容を入力してください",
                            fullWidth: true,
                            multiline: true,
                            maxRows: 5,
                            style: { width: "100%" },
                          },
                          true
                        )}
                        {/*<TextField multiline rows={4} fullWidth value={questionText} onChange={(e) => setQuestionText(e.target.value)} placeholder="質問内容を入力してください" sx={{ marginBottom: "10px", backgroundColor: "#ffffffff" }} />*/}

                        <div style={{ marginBottom: "10px" }}>※HTMLタグは使用できません。</div>
                        <div style={{ backgroundColor: "#FFFF00", padding: "10px", marginBottom: "10px" }}>
                          <ul style={{ margin: 0, paddingLeft: "20px" }}>
                            <li>
                              <strong>社名/名前などの個人情報</strong>を入力しないようにご注意ください。
                            </li>
                            <li>質問は利用規約に則ったご利用をお願いします。</li>
                            <li>
                              <strong>質問/回答は当事者間のみに公開されます。</strong>
                            </li>
                            <li>送信する前に入力内容をよく確認して下さい。一度掲載された内容は編集/削除できません。</li>
                          </ul>
                        </div>
                        {error && <p className="error">{error}</p>}
                        <p className="order-complete" style={{ margin: "10px 0", textAlign: "center" }}>
                          <s.ImportantBtn
                            //onClick={handleQuestionSubmit}
                            //disabled={sending}
                            onClick={async () => {
                              if (!form.QAnaiyo?.trim()) {
                                actions.showAlert("質問内容を入力してください");
                                return;
                              }
                              const result = await formProps.confirm();
                              if (result.result) {
                                setError("");
                              } else {
                                actions.showAlert(result?.error || "確認に失敗しました");
                              }
                            }}
                            className="important-btn"
                            style={{ cursor: "pointer" }}
                          >
                            確認する
                          </s.ImportantBtn>
                        </p>
                      </div>
                    )}
                  </s.Faq>
                </>
              )}

              {/* 確認画面 */}
              {isShowConfirm && (
                <>
                  <s.Faq>
                    <div>
                      <h3>確認</h3>
                      {error && <p className="error">{error}</p>}

                      <p style={{ whiteSpace: "pre-wrap" }}>{form.QAnaiyo}</p>

                      <div style={{ textAlign: "center", color: "#ff0000", margin: "20px 0" }}>
                        送信する前に入力内容をよく確認して下さい。 一度掲載された内容は編集/削除できません。
                      </div>

                      <p className="order-complete">
                        <s.ImportantBtn
                          //onClick={handleSend}
                          //disabled={sending}
                          onClick={async () => {
                            if (!form.QAnaiyo.trim()) {
                              actions.showAlert("質問内容を入力してください");
                              return;
                            }
                            const result = await formProps.submitQuestion();
                            if (result.result) {
                              setError("");
                              formProps.setIsShowInit(false);
                              formProps.setIsAnswerOpen(false);
                              formProps.setIsShowConfirm(false);
                              formProps.setIsShowFinish(true);
                            } else {
                              actions.showAlert(result.error || "質問の送信に失敗しました");
                            }
                          }}
                          className="important-btn"
                          style={{ cursor: "pointer" }}
                        >
                          送信する
                        </s.ImportantBtn>
                        <s.BtnGray
                          onClick={() => {
                            formProps.setIsShowInit(true);
                            formProps.setIsAnswerOpen(true);
                            formProps.setIsShowConfirm(false);
                            formProps.setIsShowFinish(false);
                          }}
                        >
                          修正する
                        </s.BtnGray>
                      </p>
                    </div>
                  </s.Faq>
                  {renderFAQSection()}
                </>
              )}

              {/* 送信完了画面 */}
              {isShowFinish && (
                <>
                  <s.Faq>
                    <h3>質問を完了しました</h3>
                    <p className="order-complete">質問を送信しました。</p>
                    <p className="order-complete">
                      <s.BtnGray onClick={onClose}>OK</s.BtnGray>
                    </p>
                  </s.Faq>
                  {renderFAQSection()}
                </>
              )}
            </>
              )}
            </s.Container>
          </DialogContent>
        </>
      ) : (
        <>
          {/* 回答用 */}
          <s.DialogTitle>
            <s.FlexBetween>
              <s.Title>見積依頼元からの質問への回答を行う</s.Title>
              <IconButton onClick={close} size="small">
                <CloseIcon />
              </IconButton>
            </s.FlexBetween>
          </s.DialogTitle>

          <DialogContent sx={{ p: 3, background: "url(/images/bg2.png) 0 0" }}>
            <s.Container>
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
            {/* 回答詳細情報 */}
              <s.OrderDetail>
                <div className="wrap">
                  <dl>
                    <dt>見積番号</dt>
                    <dd>{mitsumoriNo}</dd>
                    <dt>車名</dt>
                    <dd>{requestData?.CarName}</dd>
                    <dt>車輌型式</dt>
                    <dd>{requestData?.Sharyo_Katashiki}</dd>
                    <dt>年式</dt>
                    <dd>{requestData?.Sharyo_Nenshiki}</dd>
                    <dt className="order">見積回答部品</dt>
                    <dd>{requestData?.PartsName}</dd>
                  </dl>
                </div>
              </s.OrderDetail>
            {/* 未回答一覧情報 */}
            {isShowMikaitoList && (
              mikaitoList.length === 0 ? (
                <s.Faq>
                  <p>未回答の質問はありません。</p>
                </s.Faq>
              ) : (
                <s.Faq>
                  <p>回答を行う質問を選択してください。</p>
                  <s.SimpleTable>
                    <thead>
                      <tr>
                        <th style={{ width: "12em" }}>質問日時</th>
                        <th>質問内容</th>
                        <th style={{ width: "8em" }}>質問者地域</th>
                        <th style={{ width: "8em" }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {mikaitoList.map((qa) => (
                        <tr key={qa.Q_no}>
                          <td className="date">
                            {dayjs(qa.created).format("M月D日 H時mm分")}
                          </td>
                          <td className="txt" style={{ wordBreak: "break-all" }}>
                            {qa.QAnaiyo}
                          </td>
                          <td className="locale">{qa.Todofuken}</td>
                          <td className="ctrl">
                            {isAnswerOpen ? (
                              <s.BtnGray onClick={() => handleSelectQa(qa)}>回答する</s.BtnGray>
                            ) : (
                              "未回答"
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </s.SimpleTable>
                </s.Faq>
              )
            )}

            {/* 回答入力 */}
            {selectedQa && isAnswerOpen && (
              <s.Faq>
                <h3>質問への回答</h3>
                <dl>
                  <dt>
                    <span className="que">Q</span>
                    <span style={{ marginRight: "8px" }}>質問{selectedQa.Q_no}</span>
                    <span className="date">
                      {dayjs(selectedQa.created).format("M月D日 H時mm分")}
                    </span>
                    <span className="locale">{selectedQa.Todofuken && `(${selectedQa.Todofuken})`}</span>
                  </dt>
                  <dd>
                    <p className="txt" style={{ wordBreak: "break-all" }}>
                      {selectedQa.QAnaiyo || ""}
                    </p>
                  </dd>
                </dl>
                <p>質問への回答を入力し「確認する」ボタンを押して下さい。</p>
                {formProps.input_QAnaiyo({ fullWidth: true, multiline: true, maxRows: 10, style: { width: "100%" } }, true)}
                ※HTMLタグは使用できません。
                <s.WarningBox>
                  <ul>
                    <li>
                      <strong>社名/名前などの個人情報</strong>を入力しないようにご注意ください。
                    </li>
                    <li>質問は利用規約に則ったご利用をお願いします。</li>
                    <li>
                      <strong>質問/回答は当事者間のみに公開されます。</strong>
                    </li>
                    <li>送信する前に入力内容をよく確認して下さい。一度掲載された内容は編集/削除できません。</li>
                  </ul>
                </s.WarningBox>
                {error && <p className="error">{error}</p>}
                <p className="order-complete">
                  <s.ImportantBtn
                    onClick={async () => {
                      if (!form.QAnaiyo?.trim()) {
                        actions.showAlert("回答内容を入力してください");
                        return;
                      }
                      const result = await formProps.confirm();
                      if (result.result) {
                        setError("");
                      } else {
                        actions.showAlert(result?.error || "確認に失敗しました");
                      }
                    }}
                  >
                    確認する
                  </s.ImportantBtn>
                </p>
              </s.Faq>
            )}

            {/* 確認画面 */}
            {isShowConfirm && (
              <s.Faq>
                <h3>確認</h3>
                {error && <p className="error">{error}</p>}
                <dl>
                  <dt>
                    <span className="que">Q</span>
                    質問{selectedQa?.Q_no}
                    {selectedQa?.created && (
                      <span style={{ marginLeft: "8px" }}>
                        {dayjs(selectedQa.created).format("M月D日 H時mm分")}
                      </span>
                    )}
                    {selectedQa?.Todofuken && ` (${selectedQa.Todofuken})`}
                  </dt>
                  <dd>
                    <p style={{ whiteSpace: "pre-wrap" }}>{selectedQa?.QAnaiyo}</p>
                    <dl>
                      <dt>
                        <span className="ans">A</span> 回答
                      </dt>
                      <dd>
                        <p style={{ whiteSpace: "pre-wrap" }}>{form.QAnaiyo}</p>
                      </dd>
                    </dl>
                  </dd>
                </dl>

                <div style={{ textAlign: "center", color: "#ff0000" }}>
                  送信する前に入力内容をよく確認して下さい。一度掲載された内容は編集/削除できません。
                </div>

                <p style={{ textAlign: "center" }}>
                  <s.ImportantBtn
                    onClick={async () => {
                      if (!form.QAnaiyo.trim()) {
                        actions.showAlert("回答内容を入力してください");
                        return;
                      }

                      const result = await formProps.submitAnswer();

                      if (result.result) {
                        setError("");
                        formProps.setIsShowMikaitoList(true);
                        formProps.setIsAnswerOpen(false);
                        formProps.setIsShowConfirm(false);
                        formProps.setIsShowFinish(true);
                      } else {
                        actions.showAlert(result.error || "回答の送信に失敗しました");
                      }
                    }}
                    className="important-btn"
                    style={{ cursor: "pointer" }}
                  >
                    送信する
                  </s.ImportantBtn>
                  <s.BtnGray
                    onClick={() => {
                      formProps.setIsShowMikaitoList(true);
                      formProps.setIsAnswerOpen(true);
                      formProps.setIsShowConfirm(false);
                      formProps.setIsShowFinish(false);
                    }}
                  >
                    修正する
                  </s.BtnGray>
                </p>
              </s.Faq>
            )}

            {/* 送信完了画面 */}
            {isShowFinish && (
              <s.Faq>
                <h3>回答を完了しました</h3>
                <p className="order-complete">回答を送信しました。</p>
                <p className="order-complete">
                  <s.BtnGray onClick={onClose}>OK</s.BtnGray>
                </p>
              </s.Faq>
            )}

            {/* QA一覧情報 */}
            {renderFAQSection()}
            </>
              )}
            </s.Container>
          </DialogContent>
        </>
      )}
    </s.Dialog>
  );
}

export default QaabDialog;
